import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PinKeypad from "./PinKeypad";
import { ShieldCheck, KeyRound, ChevronLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * First-run PIN setup wizard.
 * Cannot be dismissed until a PIN is chosen — this component is only
 * rendered when the backend reports `configured === false`.
 */
export default function PinSetupWizard({ open, onDone }) {
  const [step, setStep] = useState(1); // 1 = enter, 2 = confirm
  const [first, setFirst] = useState("");
  const [second, setSecond] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep(1); setFirst(""); setSecond(""); setError(""); };

  const submitStep1 = (val) => {
    if (val.length !== 4) return;
    setFirst(val);
    setSecond("");
    setError("");
    setStep(2);
  };

  const submitStep2 = async (val) => {
    if (val.length !== 4) return;
    if (val !== first) {
      setError("Los PIN no coinciden. Vuelve a empezar.");
      setSecond("");
      setTimeout(reset, 900);
      return;
    }
    setBusy(true);
    try {
      await api.post("/security/pin/setup", { pin: val });
      toast.success("PIN maestro configurado");
      onDone?.();
    } catch (err) {
      const detail = formatApiError(err?.response?.data?.detail) || "Error al guardar el PIN";
      setError(detail);
      setSecond("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* not dismissable */ }}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden"
        data-testid="pin-setup-wizard"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="bg-gradient-to-br from-orange-50 to-white px-6 pt-6 pb-4 border-b border-orange-100">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center text-white shadow-sm">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <DialogHeader className="p-0 space-y-0">
                <DialogTitle className="text-base font-bold text-neutral-900">Configura tu PIN maestro</DialogTitle>
                <DialogDescription className="text-xs text-neutral-600">
                  Este PIN protegerá todas las operaciones sensibles de la aplicación.
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          <div className="flex items-center justify-between mb-4">
            {step === 2 && (
              <button
                onClick={() => { setStep(1); setSecond(""); setError(""); }}
                className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1"
                data-testid="pin-setup-back"
              >
                <ChevronLeft className="h-3 w-3" /> Volver
              </button>
            )}
            <div className="text-[11px] font-semibold tracking-wider text-orange-600 uppercase ml-auto">
              Paso {step} de 2
            </div>
          </div>

          <p className="text-sm text-neutral-700 mb-5">
            {step === 1
              ? "Elige un PIN de 4 dígitos. Podrás cambiarlo o recuperarlo cuando quieras."
              : "Introduce el mismo PIN una vez más para confirmarlo."}
          </p>

          {step === 1 ? (
            <PinKeypad
              value={first}
              onChange={(v) => { setFirst(v); setError(""); }}
              onSubmit={submitStep1}
              disabled={busy}
              testIdPrefix="pin-setup-1"
            />
          ) : (
            <PinKeypad
              value={second}
              onChange={(v) => { setSecond(v); setError(""); }}
              onSubmit={submitStep2}
              disabled={busy}
              testIdPrefix="pin-setup-2"
            />
          )}

          {error && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 flex items-start gap-2" data-testid="pin-setup-error">
              <KeyRound className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="mt-6 text-[11px] text-neutral-500 leading-relaxed border-t border-neutral-100 pt-3">
            <strong className="text-neutral-700">¿Por qué un PIN?</strong> Actúa como una segunda barrera
            para operaciones sensibles (eliminar citas, ajustar bonos, cambiar configuración, etc.).
            Después de introducirlo una vez, tendrás una <span className="font-semibold">sesión segura de 10 minutos</span> sin volver a pedírtelo.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
